import { useState, useCallback, useEffect, useMemo } from "react";
import { useActiveAccount } from "applesauce-react/hooks";
import { useSeoMeta } from "@unhead/react";
import { useNotifications } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Bell,
  Archive,
  Inbox,
  List,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
} from "lucide-react";
import {
  NotificationRow,
  NotificationActivityRow,
  NotificationSkeleton,
  type ViewTab,
} from "@/components/NotificationRow";
import { useNotificationPageEssentials } from "@/hooks/useNotificationPageEssentials";
import type {
  NotificationItem,
  ThreadNotificationItem,
} from "@/lib/notifications";
import type { NostrEvent } from "nostr-tools";
import { getZapAmount } from "applesauce-common/helpers";

const ITEMS_PER_PAGE = 10;

type NotificationDisplayEntry =
  | { type: "item"; item: NotificationItem; key?: string }
  | { type: "activity"; item: ThreadNotificationItem; event: NostrEvent };

export default function NotificationsPage() {
  const activeAccount = useActiveAccount();
  const { items, unreadCount, actions, history } = useNotifications();
  const [currentView, setCurrentView] = useState<ViewTab>("inbox");
  const [currentPage, setCurrentPage] = useState(1);
  const [groupByRootItem, setGroupByRootItem] = useState(true);

  useSeoMeta({
    title:
      unreadCount > 0
        ? `(${unreadCount}) Notifications - BIES Code`
        : "Notifications - BIES Code",
    description: "Your notification inbox",
    ogImage: "/og-image.png",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    twitterCard: "summary_large_image",
  });

  // Filter items by current view
  const filteredItems = useMemo(() => {
    if (!items) return undefined;
    switch (currentView) {
      case "inbox":
        return items.filter((item) => !item.archived);
      case "archived":
        return items.filter((item) => item.archived);
      case "all":
        return items;
    }
  }, [items, currentView]);

  const displayEntries = useMemo<NotificationDisplayEntry[] | undefined>(() => {
    if (!items || !filteredItems) return undefined;
    if (groupByRootItem) {
      return filteredItems.map((item) => ({ type: "item", item }));
    }

    const entries = items.flatMap<NotificationDisplayEntry>((item) => {
      // Repository stars remain repository-level notifications. Individual
      // zaps, however, need to be visible as their own activity when grouping
      // by root item is disabled.
      if (item.kind === "repo-star") {
        return filteredItems.includes(item) ? [{ type: "item", item }] : [];
      }

      const archivedIds = new Set(item.archivedEventIds);
      const events = item.events.filter((event) => {
        const isArchived = archivedIds.has(event.id);
        return currentView === "inbox"
          ? !isArchived
          : currentView === "archived"
            ? isArchived
            : true;
      });

      if (item.kind === "repo-zap") {
        return events.map((event) => ({
          type: "item",
          key: event.id,
          item: {
            ...item,
            events: [event],
            unread: item.unreadEventIds.includes(event.id),
            archived: archivedIds.has(event.id),
            archivedEventIds: archivedIds.has(event.id) ? [event.id] : [],
            latestActivity: event.created_at,
            unreadEventIds: item.unreadEventIds.includes(event.id)
              ? [event.id]
              : [],
            totalSats: Math.floor((getZapAmount(event) ?? 0) / 1000),
          },
        }));
      }

      return events.map((event) => ({ type: "activity", item, event }));
    });

    // Grouped items are ordered by their latest activity. Once expanded, each
    // activity needs its own global ordering before pagination.
    return entries.sort((a, b) => {
      const aTime =
        a.type === "activity" ? a.event.created_at : a.item.latestActivity;
      const bTime =
        b.type === "activity" ? b.event.created_at : b.item.latestActivity;
      return bTime - aTime;
    });
  }, [currentView, filteredItems, groupByRootItem, items]);

  // Pagination — reset currentPage when the list shrinks past it (#10)
  const totalPages = displayEntries
    ? Math.max(1, Math.ceil(displayEntries.length / ITEMS_PER_PAGE))
    : 1;
  const safePage = Math.min(currentPage, totalPages);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  const pageEntries = displayEntries?.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE,
  );

  const resolvedMap = useNotificationPageEssentials(
    pageEntries?.map((entry) => entry.item) ?? [],
  );

  // Reset page when switching tabs
  const handleTabChange = useCallback((tab: ViewTab) => {
    setCurrentView(tab);
    setCurrentPage(1);
  }, []);

  const handleGroupByRootItemChange = useCallback((checked: boolean) => {
    setGroupByRootItem(checked);
    setCurrentPage(1);
  }, []);

  if (!activeAccount) {
    return (
      <div className="container max-w-screen-xl px-4 md:px-8 py-16">
        <div className="flex flex-col items-center justify-center gap-4 text-center">
          <Bell className="h-12 w-12 text-muted-foreground/40" />
          <p className="text-muted-foreground">
            Sign in to see your notifications
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-screen-xl px-4 md:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold tracking-tight">Notifications</h1>

        {/* Tabs */}
        <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-muted/30 p-0.5">
          <TabButton
            active={currentView === "inbox"}
            onClick={() => handleTabChange("inbox")}
            icon={Inbox}
            label="Inbox"
            badge={unreadCount > 0 ? unreadCount : undefined}
          />
          <TabButton
            active={currentView === "archived"}
            onClick={() => handleTabChange("archived")}
            icon={Archive}
            label="Archived"
          />
          <TabButton
            active={currentView === "all"}
            onClick={() => handleTabChange("all")}
            icon={List}
            label="All"
          />
        </div>
      </div>

      {/* View and bulk actions bar */}
      <div className="mb-2 flex min-h-[32px] items-center justify-between gap-3">
        <label
          htmlFor="group-by-root-item"
          className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground"
        >
          <Checkbox
            id="group-by-root-item"
            checked={groupByRootItem}
            onCheckedChange={handleGroupByRootItemChange}
          />
          Group by root item
        </label>
        {currentView === "inbox" &&
          displayEntries &&
          displayEntries.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                onClick={actions.markAllAsRead}
              >
                <Check className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                onClick={actions.markAllAsArchived}
              >
                <Archive className="h-3 w-3 mr-1" />
                Archive all
              </Button>
            </div>
          )}
      </div>

      {/* Notification list */}
      <div className="rounded-lg border border-border/60 bg-card overflow-hidden">
        {!displayEntries || (displayEntries.length === 0 && history.loading) ? (
          // Loading skeleton — also shown when list is empty but still fetching
          // to avoid a flash of the empty state before the first page arrives
          <ul className="divide-y divide-border/40">
            {Array.from({ length: 2 }).map((_, i) => (
              <NotificationSkeleton key={i} />
            ))}
          </ul>
        ) : displayEntries.length === 0 ? (
          // Empty state
          <div className="py-16 px-8 text-center">
            <div className="max-w-sm mx-auto space-y-3">
              {currentView === "inbox" ? (
                <>
                  <Inbox className="h-10 w-10 mx-auto text-muted-foreground/30" />
                  <p className="text-muted-foreground text-sm">
                    Your inbox is empty. Notifications appear when someone
                    interacts with your repositories.
                  </p>
                </>
              ) : currentView === "archived" ? (
                <>
                  <Archive className="h-10 w-10 mx-auto text-muted-foreground/30" />
                  <p className="text-muted-foreground text-sm">
                    No archived notifications.
                  </p>
                </>
              ) : (
                <>
                  <Bell className="h-10 w-10 mx-auto text-muted-foreground/30" />
                  <p className="text-muted-foreground text-sm">
                    No notifications yet.
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {pageEntries?.map((entry) =>
              entry.type === "item" ? (
                <NotificationRow
                  key={entry.key ?? entry.item.rootId}
                  item={entry.item}
                  actions={actions}
                  currentView={currentView}
                  resolvedMap={resolvedMap}
                />
              ) : (
                <NotificationActivityRow
                  key={entry.event.id}
                  item={entry.item}
                  event={entry.event}
                  actions={actions}
                  currentView={currentView}
                  resolvedMap={resolvedMap}
                />
              ),
            )}
          </ul>
        )}
      </div>

      {/* History load-more / spinner */}
      {/* On the inbox tab, hide load more once we've fetched all non-archived
          events (reachedArchive). On archived/all tabs always show it so the
          user can page through archived history. */}
      {(history.loading ||
        (history.hasMore &&
          !(currentView === "inbox" && history.reachedArchive))) && (
        <div className="flex justify-center mt-3">
          {history.loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading older notifications…
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={history.loadMore}
            >
              Load more
            </Button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage === 1}
            onClick={() => setCurrentPage(1)}
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) =>
                p === safePage ||
                (p >= Math.max(1, safePage - 2) &&
                  p <= Math.min(totalPages, safePage + 2)),
            )
            .map((p) => (
              <Button
                key={p}
                variant={p === safePage ? "default" : "ghost"}
                size="sm"
                className="h-7 w-7 p-0 text-xs"
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </Button>
            ))}

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={safePage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab button
// ---------------------------------------------------------------------------

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      {badge !== undefined && (
        <Badge
          variant="secondary"
          className="h-4 min-w-[16px] px-1 text-[10px] leading-none"
        >
          {badge}
        </Badge>
      )}
    </button>
  );
}
